import * as React from 'react';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import type { Ind } from '@api/Entities/Ind/Ind';
import { IndStatus } from '@api/Entities/Ind/Ind';
import { Trans } from 'react-i18next';
import { ButtonTabs } from '@app/Components/ButtonTabs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { Alert } from '@app/Components/Alert';
import type { IndAPICriteria } from '@api/Repositories/Ind/IndRepository';
import { IndRepository } from '@api/Repositories/Ind/IndRepository';
import { UserFlagRepository } from '@api/Repositories/User/UserFlagRepository';
import { LoadingPromptAlert } from '@app/Components/LoadingPrompt';
import { t } from 'i18next';
import { IndMiniCard } from '@app/Components/Ind/IndMiniCard';
import { useAuth } from '@app/Contexts/AuthProvider';
import { Form } from 'react-bootstrap';
import { getAlphabet } from '@app/Utilities/String';
import type { UserFlag } from '@api/Entities/User/UserFlag';
import { toggleInArray } from '@app/Utilities/Array';
import { UserRepository } from '@api/index';
import { useRepository } from '@app/Hooks/useRepository';
import { UserFlagButton } from './User/UserFlagButton';

enum ClientTabs {
  Clients = 'clients',
  Prospects = 'prospects',
  Flagged = 'flagged',
}

const clientTabs = [
  { key: ClientTabs.Clients, label: 'Clients', activeVariant: 'success' },
  { key: ClientTabs.Prospects, label: 'Prospects', activeVariant: 'primary' },
  { key: ClientTabs.Flagged, label: 'Flagged', activeVariant: 'danger' },
];

type ProfileSearchs = {
  keywords: string;
  letter: string;
  flags: Array<string>;
};

const ALPHABET = getAlphabet();

export const ProfileSearch = () => {
  const session = useAuth();
  const indRepository = useRepository(IndRepository);
  const userFlagRepository = useRepository(UserFlagRepository);

  const [activeTab, setActiveTab] = React.useState<ClientTabs>(ClientTabs.Clients);

  const [individuals, setInds] = React.useState<Array<Ind>>([]);
  const [individualsLoading, setIndsLoading] = React.useState<boolean>(true);

  const [userFlags, setUserFlags] = React.useState<Array<UserFlag>>([]);
  const [userFlagsLoading, setUserFlagsLoading] = React.useState<boolean>(true);

  const [filters, setFilters] = React.useState<ProfileSearchs>({
    keywords: '',
    letter: '',
    flags: [],
  });

  const handleLetterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setFilters((prev) => ({ ...prev, letter: value }));
  };

  const handleFlagToggle = (flag: string) => {
    setFilters((prev) => {
      return {
        ...prev,
        flags: toggleInArray(prev.flags, flag),
      };
    });
  };

  React.useEffect(() => {
    setUserFlagsLoading(true);

    userFlagRepository.getUserFlags(session.getUser().getUserId()).then((userFlags) => {
      setUserFlags(userFlags.getItems());
      setUserFlagsLoading(false);
    });
  }, []);

  React.useEffect(() => {
    setIndsLoading(true);

    const abortController = new AbortController();
    const criteria: IndAPICriteria = {};

    if (activeTab === ClientTabs.Flagged) {
      criteria.indStatus = {
        in: [IndStatus.Active, IndStatus.Prospect],
      };
    } else {
      criteria.indStatus = {
        eq: activeTab === ClientTabs.Clients ? IndStatus.Active : IndStatus.Prospect,
      };
    }

    indRepository
      .getInds(
        { signal: abortController.signal, include: ['addresses', 'emails', 'phones', 'flags'] },
        criteria
      )
      .then((individualCollection) => {
        setInds(individualCollection.getItems());
        setIndsLoading(false);
      });

    return () => abortController.abort();
  }, [activeTab]);

  // --- Render helpers -------------------------------------------------------

  const AlertLoading = () => (
    <LoadingPromptAlert variant="secondary" size="sm">
      {t('common.loading')}...
    </LoadingPromptAlert>
  );

  const AlertNoResults = () => (
    <Alert variant="secondary" size="sm">
      {t('dashboard.clientDrawer.noResults')}
    </Alert>
  );

  const ReportingPrompt = () => (
    <Alert variant="secondary" size="sm">
      <FontAwesomeIcon icon={faExclamationCircle} className="me-2" />
      <Trans
        i18nKey="dashboard.clientDrawer.reportingPrompt"
        components={[<Link to="/agent/my-book" />]}
      />
    </Alert>
  );

  const LetterFilter = () => (
    <Form.Group className="mb-4">
      <Form.Label>{t('dashboard.clientDrawer.form.filterLabel')}</Form.Label>

      <select className="form-select" value={filters.letter} onChange={handleLetterChange}>
        <option value="">{t('dashboard.clientDrawer.form.filterPlaceholder')}</option>
        {ALPHABET.map((letter) => (
          <option key={letter} value={letter}>
            {letter}
          </option>
        ))}
      </select>
    </Form.Group>
  );

  const FlagFilterContent = () => {
    if (userFlagsLoading) {
      return <AlertLoading />;
    }

    if (userFlags.length === 0) {
      return (
        <Alert variant="secondary" size="sm">
          {t('dashboard.clientDrawer.form.flagNoResults')}
        </Alert>
      );
    }

    return (
      <div>
        {userFlags.map((flag) => {
          const selected = filters.flags.includes(flag.getFlagColor());

          return (
            <UserFlagButton
              key={flag.getFlagColor()}
              flag={flag}
              selected={selected}
              onToggle={handleFlagToggle}
              className="me-2 mb-2"
            />
          );
        })}
      </div>
    );
  };

  const FlagFilter = () => {
    if (activeTab !== ClientTabs.Flagged) return null;

    return (
      <Form.Group className="mb-4">
        <Form.Label>{t('dashboard.clientDrawer.form.flagLabel')}</Form.Label>
        <FlagFilterContent />
      </Form.Group>
    );
  };

  const IndsList = () => (
    <div className="fill-scroll-x">
      {individuals.map((individual) => (
        <IndMiniCard
          key={individual.getIndId()}
          individual={individual}
          show={activeTab === ClientTabs.Flagged ? ['flags'] : undefined}
        />
      ))}
    </div>
  );

  const showEmptyState = !individualsLoading && individuals.length === 0;
  const showMainContent = !individualsLoading && individuals.length > 0;

  return (
    <>
      <div>
        <ButtonTabs<ClientTabs>
          tabs={clientTabs}
          activeKey={activeTab}
          onSelect={setActiveTab}
          inactiveVariant="secondary"
          className="mb-3"
        />
      </div>

      {individualsLoading && <AlertLoading />}

      {showEmptyState && <AlertNoResults />}

      {showMainContent && (
        <>
          <ReportingPrompt />

          <Form>
            <LetterFilter />
            <FlagFilter />
          </Form>

          <IndsList />
        </>
      )}
    </>
  );
};
